/**
 * Theme Creator procedural music — shared helpers + editor preview player.
 * In-game playback is wired through legacy-app startMenuMusic / startGameMusic.
 */
import {
  themeMusicIsActive,
  themeMusicMidiToFreq,
  themeMusicNoteToMidi,
  themeMusicStepMs,
  themeMusicTotalSteps,
  type ThemeMusicData,
  type ThemeMusicOscillator,
} from "./theme-types";

export {
  themeMusicIsActive,
  themeMusicMidiToFreq,
  themeMusicNoteToMidi,
  themeMusicStepMs,
  themeMusicTotalSteps,
};

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

export function themeMusicNoteLabel(noteIndex: number): string {
  const midi = themeMusicNoteToMidi(noteIndex);
  const name = NOTE_NAMES[((midi % 12) + 12) % 12];
  const octave = Math.floor(midi / 12) - 1;
  return `${name}${octave}`;
}

/** Notes sounding on a given absolute step. */
export function themeMusicNotesAtStep(music: ThemeMusicData, step: number): number[] {
  const total = themeMusicTotalSteps(music);
  const s = ((step % total) + total) % total;
  const out: number[] = [];
  for (const c of music.cells) {
    if (c.step === s) out.push(c.note);
  }
  return out;
}

export type ThemeMusicToneFn = (
  freq: number,
  when: number,
  duration: number,
  gainValue: number,
  type: ThemeMusicOscillator,
) => void;

/** Trigger all notes for one tracker step (chords = multiple notes). */
export function playThemeMusicStep(
  music: ThemeMusicData,
  step: number,
  when: number,
  gainValue: number,
  playTone: ThemeMusicToneFn,
): void {
  if (!themeMusicIsActive(music) || gainValue <= 0) return;
  const notes = themeMusicNotesAtStep(music, step);
  if (!notes.length) return;
  const stepSec = themeMusicStepMs(music.bpm) / 1000;
  const dur = Math.max(0.05, stepSec * 0.78);
  const perNote = gainValue / Math.sqrt(notes.length);
  for (const note of notes) {
    const freq = themeMusicMidiToFreq(themeMusicNoteToMidi(note));
    playTone(freq, when, dur, perNote, music.oscillator);
  }
}

export function themeMusicAppliesToMenu(music: ThemeMusicData | null | undefined): boolean {
  return themeMusicIsActive(music) && (music!.scope === "menu" || music!.scope === "both");
}

export function themeMusicAppliesToGame(music: ThemeMusicData | null | undefined): boolean {
  return themeMusicIsActive(music) && (music!.scope === "game" || music!.scope === "both");
}

/** Lightweight loop for the Theme Creator Music editor (own AudioContext). */
export class ThemeMusicPreviewPlayer {
  private ctx: AudioContext | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private step = 0;
  private music: ThemeMusicData | null = null;
  private onStep: ((step: number) => void) | null = null;
  private volume = 0.045;

  get playing(): boolean {
    return this.timer != null;
  }

  get currentStep(): number {
    return this.step;
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(0.12, v));
  }

  async ensureCtx(): Promise<AudioContext> {
    if (!this.ctx) {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new AC();
    }
    if (this.ctx.state !== "running") {
      await this.ctx.resume().catch(() => {});
    }
    return this.ctx;
  }

  private playTone: ThemeMusicToneFn = (freq, when, duration, gainValue, type) => {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== "running" || gainValue <= 0) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, when);
    gain.gain.setValueAtTime(0.0001, when);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, gainValue), when + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, when + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(when);
    osc.stop(when + duration + 0.03);
  };

  start(music: ThemeMusicData, onStep?: (step: number) => void): void {
    this.stop();
    if (!themeMusicIsActive(music)) return;
    this.music = music;
    this.onStep = onStep || null;
    this.step = 0;
    void this.ensureCtx().then((ctx) => {
      if (!this.music) return;
      const tick = () => {
        if (!this.music || !this.timer) return;
        if (ctx.state !== "running") {
          this.stop();
          return;
        }
        const when = ctx.currentTime + 0.02;
        playThemeMusicStep(this.music, this.step, when, this.volume, this.playTone);
        this.onStep?.(this.step);
        this.step = (this.step + 1) % themeMusicTotalSteps(this.music);
        this.timer = setTimeout(tick, themeMusicStepMs(this.music.bpm));
      };
      this.timer = setTimeout(tick, 0);
    });
  }

  /** Hot-swap composition while playing (keeps step position when possible). */
  updateMusic(music: ThemeMusicData): void {
    this.music = music;
    if (!themeMusicIsActive(music) && this.timer) this.stop();
  }

  stop(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.onStep = null;
  }

  dispose(): void {
    this.stop();
    if (this.ctx) {
      void this.ctx.close().catch(() => {});
      this.ctx = null;
    }
  }
}
